import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { UseOutletContextStorageCenter } from './StorageCenter';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { StorageCenterDA } from '../../../../services/admin/storage.center.service';
import { Error } from '../../../../models/error.model';
import { StorageOperationDA } from '../../../../services/admin/storage.operation.service';
import { Operation } from '../../../../models/operation.model';
import { CompanyStorageOperations } from '../../../../models/company.storage.operations.model';
import { StorageCenter } from '../../../../models/storage.center.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { formatAddress } from '../../../../scripts/format.value.script';
import { sortColumn } from '../../../../scripts/table.script';
import { modalShow } from '../../../../utils/modal.util';
import { validationValue } from '../../../../libraries/validation.library';
import { operationTypeValue } from '../../../../libraries/operation.type.library';
import { sourceTypeValue, sourceTypeList } from '../../../../libraries/source.type.library';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';

import AppPreloader from '../../../../components/Preloader';
import AppSortTable from '../../../../components/element/SortTable';
import AppPagination from '../../../../components/element/Pagination';
import AppModalStorageOperation from '../../../../components/modal/ModalStorageOperation';

export interface StorageCenterOperationPageProps {};

let errorResponse: Error, storageCenterResponse: StorageCenter, companyStorageOperationsResponse: CompanyStorageOperations;

const StorageCenterOperationPage: React.FunctionComponent<StorageCenterOperationPageProps> = props => {
  const {setRoute, companyForUser, storageCentersForCompany} = UseOutletContextStorageCenter()
  const {lang} = StateLanguage()
  const param = useParams()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({sort_field: 'created_at', sort_mode: 'desc', type: '', source_type: '', search: '', date_from: '', date_to: ''})
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [operations, setOperations] = useState<Operation[] | null>(null)
  const [storageCenter, setStorageCenter] = useState<StorageCenter | null>(null)

  const filterRef = useRef(filter)
  filterRef.current = filter

  const [searchText, setSearchText] = useState(filter.search)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchText(value)

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)

    searchTimerRef.current = setTimeout(() => {
      const newFilter = {...filter, search: value}
      setFilter(newFilter)
      filterRef.current = newFilter
      if (companyId && storageCenterId) loadOperations(companyId, storageCenterId, 1)
    }, 400)
  }

  const loadStorageCenter = async (id_company: string, id_storage_center: string) => {
    await StorageCenterDA.getStorageCenter(id_company, id_storage_center).then( (response) => {
      if (response.status === 200) {
        storageCenterResponse = response.data?.data ?? response.data
        setStorageCenter(storageCenterResponse)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadOperations = async (id_company: string, id_storage_center: string, page: number) => {
    const f = filterRef.current
    await StorageOperationDA.getStorageOperations(id_company, id_storage_center, f.sort_field, f.sort_mode, page, pagination.limit, f.type, f.date_from, f.date_to, f.search).then( (response) => {
      if (response.status === 200) {
        companyStorageOperationsResponse = response.data

        const items = companyStorageOperationsResponse.items ?? companyStorageOperationsResponse.operations ?? []

        // Client-side source_type filter (not supported by backend)
        const filtered = f.source_type
          ? items.filter((op: any) => (op.source?.type ?? op.source?.entity_type ?? '') === f.source_type)
          : items

        const paginationData = companyStorageOperationsResponse.pagination
        const totalPages = paginationData?.pages || 1

        if (page > totalPages && totalPages >= 1) {
          setUrlPage(totalPages)
          loadOperations(id_company, id_storage_center, totalPages)
          return
        }

        let pagesOfPagination: number[] = []
        for (let i = 1; i <= totalPages; i++) {
          pagesOfPagination.push(i)
        }

        const resolvedPage = paginationData?.page || 1
        setOperations(filtered)
        setPagination({...pagination, current: resolvedPage, total: paginationData?.total || items.length, pages: pagesOfPagination})
        if (resolvedPage !== urlPage) setUrlPage(resolvedPage)
      } else {
        errorResponse = response.data
        setOperations([])

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      setOperations([])
      errorWebHandler(lang, error)
    })
  }

  const loadPagination = (current: number) => {
    if (!companyId || !storageCenterId) return
    setUrlPage(current)
    loadOperations(companyId, storageCenterId, current)
  }

  const executeSortList = (item: string) => {
    if (companyId && storageCenterId) {
      const newFilter = {...filterRef.current}
      sortColumn(item, newFilter)
      setFilter(newFilter)
      filterRef.current = newFilter
      setUrlPage(1)
      loadOperations(companyId, storageCenterId, 1)
    }
  }

  const executeFilterType = (type: string) => {
    if (companyId && storageCenterId) {
      const newFilter = {...filter, type: filter.type === type ? '' : type}
      setFilter(newFilter)
      filterRef.current = newFilter
      setOperations(null)
      setUrlPage(1)
      loadOperations(companyId, storageCenterId, 1)
    }
  }

  const executeFilterSourceType = (sourceType: string) => {
    if (companyId && storageCenterId) {
      const newVal = filter.source_type === sourceType ? '' : sourceType
      const newFilter = {...filter, source_type: newVal}
      setFilter(newFilter)
      filterRef.current = newFilter
      setOperations(null)
      setUrlPage(1)
      loadOperations(companyId, storageCenterId, 1)
    }
  }

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFilter = {...filter, date_from: e.target.value}
    setFilter(newFilter)
    filterRef.current = newFilter
    if (companyId && storageCenterId) {
      setUrlPage(1)
      loadOperations(companyId, storageCenterId, 1)
    }
  }

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFilter = {...filter, date_to: e.target.value}
    setFilter(newFilter)
    filterRef.current = newFilter
    if (companyId && storageCenterId) {
      setUrlPage(1)
      loadOperations(companyId, storageCenterId, 1)
    }
  }

  function sourceEntity(source: any): {link: string, display: string} {
    if (!source) return {link: '', display: ''}
    const sourceType = source.type ?? source.entity_type ?? ''
    const entityId = source.id ?? source.entity?.id ?? ''
    const entityNumber = source.number ?? ''

    switch (sourceType) {
      case 'provider-order':
        return {link: entityId ? `/main/purchase/order/view/${entityId}` : '', display: entityNumber || source.entity?.provider_order_number || ''}
      case 'provider-invoice':
        return {link: entityId ? `/main/purchase/invoice/view/${entityId}` : '', display: entityNumber || source.entity?.provider_invoice_number || ''}
      case 'order':
        return {link: entityId ? `/main/sale/order/view/${entityId}` : '', display: entityNumber || source.entity?.order_number || ''}
      case 'invoice':
        return {link: entityId ? `/main/sale/invoice/view/${entityId}` : '', display: entityNumber || source.entity?.invoice_number || ''}
      case 'sale':
        return {link: entityId ? `/main/pos/sale/view/${entityId}` : '', display: entityNumber || source.entity?.sale_number || ''}
      case 'delivery-note':
        return {link: source.id_parent ? `/main/sale/order/${source.id_parent}/delivery-note/${entityId}` : '', display: entityNumber || source.entity?.delivery_note_number || ''}
      case 'consignment':
      case 'consignment-return':
        return {link: entityId ? `/main/purchase/consignment/view/${entityId}` : '', display: entityNumber || source.entity?.consignment_number || ''}
      default:
        return {link: '', display: entityNumber || source?.type || '-'}
    }
  }

  const companyId = companyForUser?.id
  const storageCenterId = param.id

  useEffect( () => {
    if (companyId && storageCenterId) {
      loadStorageCenter(companyId, storageCenterId)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, storageCenterId])

  useEffect( () => {
    if (companyId && storageCenterId) {
      setOperations(null)
      loadOperations(companyId, storageCenterId, urlPage)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, storageCenterId])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.inventory, branch: lang.labels.storageOperations}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && storageCentersForCompany && storageCenter && operations
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h3 className="fw-bold">{lang.labels.listOperationsStorageCenter}</h3>
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end align-items-center">
                  <button className="btn btn-light-success me-2" type="button" onClick={() => modalShow('modal-storage-operation-in')}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12C22 17.5 17.5 22 12 22ZM11 8V16H13V8H11ZM8 11V13H16V11H8Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.stockEntry}
                    </div>
                  </button>
                  <button className="btn btn-light-danger" type="button" onClick={() => modalShow('modal-storage-operation-out')}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12C22 17.5 17.5 22 12 22ZM8 11V13H16V11H8Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.stockExit}
                    </div>
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="d-flex align-items-center">
                <div className="symbol symbol-square me-5">
                  <div className="symbol-label bg-transparent border border-secondary border-dashed w-40px h-40px">
                    <span className="svg-icon svg-icon-2x svg-icon-primary">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity="0.3" d="M18.0624 15.3454L13.1624 20.7453C12.5624 21.4453 11.5624 21.4453 10.9624 20.7453L6.06242 15.3454C4.56242 13.6454 3.76242 11.4452 4.06242 8.94525C4.56242 5.34525 7.46242 2.44534 11.0624 2.04534C15.8624 1.54534 19.9624 5.24525 19.9624 9.94525C20.0624 12.0452 19.2624 13.9454 18.0624 15.3454ZM13.0624 10.0453C13.0624 9.44534 12.6624 9.04534 12.0624 9.04534C11.4624 9.04534 11.0624 9.44534 11.0624 10.0453V13.0453H13.0624V10.0453Z" fill="black"/>
                        <path d="M12.6624 5.54531C12.2624 5.24531 11.7624 5.24531 11.4624 5.54531L8.06241 8.04531V12.0453C8.06241 12.6453 8.46241 13.0453 9.06241 13.0453H11.0624V10.0453C11.0624 9.44531 11.4624 9.04531 12.0624 9.04531C12.6624 9.04531 13.0624 9.44531 13.0624 10.0453V13.0453H15.0624C15.6624 13.0453 16.0624 12.6453 16.0624 12.0453V8.04531L12.6624 5.54531Z" fill="black"/>
                      </svg>
                    </span>
                  </div>
                </div>
                <div className="d-flex flex-column">
                  <span className="fs-5 fw-bold">{storageCenter.name}</span>
                  <span className="small text-muted">{formatAddress(lang, storageCenter.address)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="card card-flush">
            <div className="card-header border-0 pt-5">
              <div className="card-title">{lang.labels.storageOperations}</div>
              <div className="card-toolbar">
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <div className="position-relative">
                    <span className="svg-icon svg-icon-3 position-absolute top-50 translate-middle-y ms-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"/>
                        <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"/>
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-sm ps-10"
                      style={{width: '180px'}}
                      placeholder={lang.labels.search}
                      value={searchText}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <input type="date" className="form-control form-control-sm" style={{width: '140px'}} value={filter.date_from} onChange={handleDateFromChange} />
                  <input type="date" className="form-control form-control-sm" style={{width: '140px'}} value={filter.date_to} onChange={handleDateToChange} />
                  <button className={`btn btn-sm ${filter.type === 'in' ? 'btn-success' : 'btn-light-success'}`} type="button" onClick={() => executeFilterType('in')}>{lang.labels.in}</button>
                  <button className={`btn btn-sm ${filter.type === 'out' ? 'btn-danger' : 'btn-light-danger'}`} type="button" onClick={() => executeFilterType('out')}>{lang.labels.out}</button>
                </div>
              </div>
            </div>
            <div className="card-header border-0 pt-0 pb-3">
              <div className="card-toolbar">
                <div className="d-flex gap-1 align-items-center flex-wrap">
                  {sourceTypeList(lang).map(s => (
                    <button
                      key={s.code}
                      className={`btn btn-sm ${filter.source_type === s.code ? 'btn-primary' : 'btn-light'}`}
                      type="button"
                      onClick={() => executeFilterSourceType(s.code)}
                    >
                      {s.display}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="dataTables_wrapper">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed mb-2">
                    <thead className="border-bottom">
                      <tr className="text-muted text-uppercase fw-bolder fs-7">
                        <th className="w-125px">
                          <div className="d-flex align-items-center justify-content-start text-nowrap cursor-pointer" onClick={() => executeSortList("created_at")}>
                            <span className="me-1">{lang.labels.date}</span>
                            <AppSortTable attribute={"created_at"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-125px">
                          <div className="d-flex align-items-center justify-content-start text-nowrap cursor-pointer" onClick={() => executeSortList("type")}>
                            <span className="me-1">{lang.labels.type}</span>
                            <AppSortTable attribute={"type"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-125px">
                          <div className="d-flex align-items-center justify-content-start text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                            <span className="me-1">{lang.labels.status}</span>
                            <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-200px">
                          <div className="d-flex align-items-center justify-content-start text-nowrap cursor-pointer" onClick={() => executeSortList("source")}>
                            <span className="me-1">{lang.labels.source}</span>
                            <AppSortTable attribute={"source"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-200px">
                          <div className="d-flex align-items-center justify-content-end text-nowrap">
                            <span className="me-1">{lang.labels.sourceLocation}</span>
                          </div>
                        </th>
                        <th className="min-w-200px">
                          <div className="d-flex align-items-center justify-content-end text-nowrap">
                            <span className="me-1">{lang.labels.destinationLocation}</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    { pagination.total > 0
                      ?
                      <tbody className="text-secondary fw-bold">
                        { operations.map (( (item, index) => { return (
                          <tr key={index} className="row-dashed">
                            <td className="text-dark text-nowrap">{dateFormat(lang.code, item.created_at)}</td>
                            <td>{operationTypeValue(lang, item.type)}</td>
                            <td>
                              <span className={`badge badge-light-${item.status === "validated" ? "success" : "warning"} fs-7`}>{validationValue(lang, item.status)}</span>
                            </td>
                            <td className="text-nowrap">
                              {sourceTypeValue(lang, item.source?.type ?? item.source?.entity_type ?? '')}
                              { item.source && sourceEntity(item.source).link.length > 0 &&
                                <Link to={sourceEntity(item.source).link} className="small ms-1">[{sourceEntity(item.source).display}]</Link>
                              }
                            </td>
                            <td className="text-end">{item.source_location ? item.source_location.name : lang.labels.none}</td>
                            <td className="text-end">{item.destination_location ? item.destination_location.name : lang.labels.none}</td>
                          </tr>
                        )}))}
                      </tbody>
                      :
                      <tbody>
                        <tr className="text-secondary fw-bold">
                          <td colSpan={6} className="text-center">{lang.labels.noRecordsFound}</td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
                <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>
              </div>
            </div>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalStorageOperation companyForUser={companyForUser} operationType="in" preselectedStorageCenter={storageCenterId} storageCentersForCompany={storageCentersForCompany} onSuccess={() => { if (companyId && storageCenterId) { loadStorageCenter(companyId, storageCenterId); loadOperations(companyId, storageCenterId, 1) } }}></AppModalStorageOperation>
      <AppModalStorageOperation companyForUser={companyForUser} operationType="out" preselectedStorageCenter={storageCenterId} storageCentersForCompany={storageCentersForCompany} onSuccess={() => { if (companyId && storageCenterId) { loadStorageCenter(companyId, storageCenterId); loadOperations(companyId, storageCenterId, 1) } }}></AppModalStorageOperation>
    </div>
  )
};

export default StorageCenterOperationPage;
